// «Скрыть идею» (замечание Айталы A-26).
//
// Как ведёт себя: на странице идеи покупатель жмёт «Скрыть» — его возвращает
// назад, и на стене эта идея видна скрытой (бледная плитка с перечёркнутым
// глазом). Нажатие на глаз возвращает идею. После обновления страницы скрытая
// идея со стены пропадает.
//
// Где хранится: на шлюзе (таблица hidden_ideas) — по гостевому clientId
// (тот же, что у гостевых лайков) или по аккаунту покупателя; при входе шлюз
// переносит гостевые скрытия в аккаунт. Раньше кнопка была чисто локальным
// useState и после перезагрузки ничего не помнила.
//
// Два слоя:
// - `serverRefs` — что было скрыто к загрузке страницы (запрос ниже). По нему
//   стены выкидывают идеи; ссылки шлюз отдаёт как у лайков: `slug || id`.
// - `session` — что скрыли или вернули за эту сессию страницы (в памяти, без
//   localStorage). По нему стена рисует вуаль, а не выкидывает идею. Хранит
//   снимок идеи и её место в ленте: если лента успеет перезапроситься (у
//   покупателя шлюз сам убирает скрытые из /feed/collections), плитка
//   остаётся на месте до перезагрузки.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";

import { api, type FeedCollection } from "@/api";
import { getBuyerAuthToken } from "@/api/token";

import { useProfile } from "./profile";
import { ideaLikeId, useWishlist } from "./wishlist";

type SessionEntry = {
	collection: FeedCollection;
	hidden: boolean;
	/** Место в общей ленте (/feed/collections) на момент скрытия. */
	index: number;
};

type HiddenIdeasState = {
	session: Record<string, SessionEntry>;
	mark: (collection: FeedCollection, hidden: boolean, index: number) => void;
	unmark: (id: string) => void;
};

const useHiddenSession = create<HiddenIdeasState>()((set) => ({
	session: {},
	mark: (collection, hidden, index) =>
		set((s) => ({ session: { ...s.session, [collection.id]: { collection, hidden, index } } })),
	unmark: (id) =>
		set((s) => {
			const next = { ...s.session };
			delete next[id];
			return { session: next };
		}),
}));

const FEED_KEY = ["feed", "collections"] as const;

/** Ключ запроса скрытых: у гостя — его clientId, у покупателя — аккаунт. */
const useViewerKey = (): string => {
	const token = useProfile((s) => s.token);
	const userId = useProfile((s) => s.user?.id);
	const clientId = useWishlist((s) => s.clientId);
	return token ? `buyer:${userId ?? ""}` : `guest:${clientId}`;
};

const fetchHidden = async (): Promise<string[]> => {
	const state = getBuyerAuthToken()
		? await api.buyer.listHiddenIdeas()
		: await api.engagement.getHiddenIdeas(useWishlist.getState().clientId);
	return state.ids;
};

/** Скрытые к загрузке страницы. Ошибка запроса = «ничего не скрыто»: лента публична. */
const useServerHidden = () => {
	const viewer = useViewerKey();
	return useQuery({
		queryKey: ["ideas", "hidden", viewer],
		queryFn: fetchHidden,
		// Список нужен ровно один раз на загрузку страницы: свежие скрытия
		// живут в `session`, и перезапрос только сбил бы вуаль на стене.
		staleTime: Number.POSITIVE_INFINITY,
		retry: false,
	});
};

const refers = (refs: readonly string[], c: Pick<FeedCollection, "id" | "slug">): boolean =>
	refs.includes(c.id) || refs.includes(c.slug);

/** Скрыта ли идея сейчас (для кнопки на странице идеи). */
export const useIdeaHidden = (c: Pick<FeedCollection, "id" | "slug">): boolean => {
	const entry = useHiddenSession((s) => s.session[c.id]);
	const server = useServerHidden();
	if (entry) return entry.hidden;
	return refers(server.data ?? [], c);
};

/** Вуаль «скрыто» на плитке: только для скрытых в этой сессии. */
export const useIdeaHiddenInSession = (id: string): boolean => useHiddenSession((s) => s.session[id]?.hidden ?? false);

/**
 * Стена без скрытых идей. Скрытые до загрузки страницы выпадают, скрытые в
 * этой сессии остаются на своём месте (вуаль рисует IdeaCard). `pending` —
 * пока не знаем, что скрыто: стена показывает скелет, а не мигает идеей,
 * которая через миг исчезнет.
 */
export const useVisibleIdeas = (list: FeedCollection[] | undefined): { ideas: FeedCollection[]; pending: boolean } => {
	const session = useHiddenSession((s) => s.session);
	const server = useServerHidden();
	const refs = server.data ?? [];
	const ideas = [...(list ?? [])];
	// Скрытая в сессии идея могла уйти из ленты после перезапроса — возвращаем
	// снимок на прежнее место, пока страницу не обновили.
	if (list) {
		for (const entry of Object.values(session).sort((a, b) => a.index - b.index)) {
			if (ideas.some((c) => c.id === entry.collection.id)) continue;
			ideas.splice(Math.min(entry.index, ideas.length), 0, entry.collection);
		}
	}
	return {
		ideas: ideas.filter((c) => session[c.id] != null || !refers(refs, c)),
		pending: server.isPending,
	};
};

/** Скрыть/вернуть идею: сразу в сессию, затем запись на шлюз; ошибка — откат. */
export const useSetIdeaHidden = () => {
	const queryClient = useQueryClient();
	const mark = useHiddenSession((s) => s.mark);
	const unmark = useHiddenSession((s) => s.unmark);
	return async (collection: FeedCollection, hidden: boolean): Promise<void> => {
		const prev = useHiddenSession.getState().session[collection.id];
		const feed = queryClient.getQueryData<FeedCollection[]>(FEED_KEY) ?? [];
		const found = feed.findIndex((c) => c.id === collection.id);
		mark(collection, hidden, prev?.index ?? (found >= 0 ? found : feed.length));
		try {
			// Ключ — как у лайков идей: `slug || id` (шлюз принимает и id).
			const ref = ideaLikeId(collection);
			if (getBuyerAuthToken()) await api.buyer.setIdeaHidden(ref, hidden);
			else await api.engagement.setIdeaHidden(useWishlist.getState().clientId, ref, hidden);
		} catch (error) {
			if (prev) mark(prev.collection, prev.hidden, prev.index);
			else unmark(collection.id);
			throw error;
		}
	};
};
